import React from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import { darkenColor, adjustOpacity } from '../../../theme';

/**
 * StatBox Component
 *
 * A compact colored box component for displaying statistics with value and label.
 * Enhanced with modern gradient backgrounds similar to ExperimentResults and MetricCard.
 *
 * @param {string|number} value - The main value to display
 * @param {string} label - The label/description text
 * @param {string} color - MUI color theme (primary, success, warning, error, info, secondary, grey)
 * @param {object} sx - Additional styling overrides
 * @param {string} valueVariant - Typography variant for value
 * @param {boolean} flex - Whether to use flex: 1 for equal width distribution
 */
const StatBox = ({
  value,
  label,
  color = 'primary',
  sx = {},
  valueVariant = 'h5',
  flex = false,
}) => {
  const theme = useTheme();

  // Get theme colors for the specified color variant
  const getColorGradient = colorName => {
    // Handle grey color specially (use grey palette)
    if (colorName === 'grey') {
      const greyColor =
        theme.palette.grey[600] ||
        theme.palette.grey[500] ||
        theme.palette.text.secondary;
      const darkGrey = theme.palette.grey[800] || darkenColor(greyColor, 20);

      return {
        background: `linear-gradient(135deg, ${greyColor} 0%, ${darkGrey} 100%)`,
        shadow: adjustOpacity(greyColor, 0.2),
        shadowHover: adjustOpacity(greyColor, 0.3),
      };
    }

    const paletteColor = theme.palette[colorName] || theme.palette.primary;
    const mainColor = paletteColor.main;
    const darkColor = paletteColor.dark || darkenColor(mainColor, 20);

    return {
      background: `linear-gradient(135deg, ${mainColor} 0%, ${darkColor} 100%)`,
      shadow: adjustOpacity(mainColor, 0.2),
      shadowHover: adjustOpacity(mainColor, 0.3),
    };
  };

  // Color gradient mapping using theme colors
  const colorGradients = {
    primary: getColorGradient('primary'),
    success: getColorGradient('success'),
    warning: getColorGradient('warning'),
    error: getColorGradient('error'),
    info: getColorGradient('info'),
    secondary: getColorGradient('secondary'),
    grey: getColorGradient('grey'),
  };

  const gradient = colorGradients[color] || colorGradients.primary;
  const contrastText =
    color === 'grey'
      ? theme.palette.common.white
      : theme.palette[color]?.contrastText || theme.palette.common.white;

  return (
    <Box
      sx={{
        ...(flex && { flex: 1 }),
        textAlign: 'center',
        p: 1.5,
        background: gradient.background,
        color: contrastText,
        borderRadius: 1.5,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        minHeight: 70,
        boxShadow: `0 2px 8px ${gradient.shadow}`,
        transition: 'all 0.3s ease',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: `0 4px 12px ${gradient.shadowHover}`,
        },
        ...sx,
      }}
    >
      <Typography
        variant={valueVariant}
        sx={{ color: contrastText, mb: 0.5, fontWeight: 600 }}
      >
        {value}
      </Typography>
      <Typography
        variant="caption"
        sx={{
          color: adjustOpacity(contrastText, 0.9),
          fontSize: '0.75rem',
        }}
      >
        {label}
      </Typography>
    </Box>
  );
};

export default StatBox;
